import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { StickyNote, Tag, Trash2, Plus, BarChart3, Loader2 } from "lucide-react";
import { tzOpts } from "@/lib/tz";

// ---------------------------------------------------------------- alerta sonoro
const SOUND_KEY = "cz_alert_sound";

export function soundEnabled() {
  try { return localStorage.getItem(SOUND_KEY) !== "off"; } catch { return true; }
}
export function setSoundEnabled(on) {
  try { localStorage.setItem(SOUND_KEY, on ? "on" : "off"); } catch { /* sem armazenamento */ }
}

let audioCtx = null;
// Dois "bips" curtos. O navegador so libera audio depois de o usuario ter mexido na pagina.
export function playBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    audioCtx = audioCtx || new Ctx();
    if (audioCtx.state === "suspended") audioCtx.resume();
    [0, 0.22].forEach((delay) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, audioCtx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.25, audioCtx.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + delay + 0.18);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(audioCtx.currentTime + delay);
      osc.stop(audioCtx.currentTime + delay + 0.2);
    });
  } catch { /* sem audio */ }
}

// Titulo da aba piscando ("🔔 Novo atendimento") ate a pessoa voltar pra janela.
export function useTitleBlink(active, text) {
  const baseRef = useRef(null);
  useEffect(() => {
    if (!active) return undefined;
    if (baseRef.current === null) baseRef.current = document.title;
    let on = true;
    const t = setInterval(() => {
      document.title = on ? text : baseRef.current;
      on = !on;
    }, 1000);
    const stop = () => { if (!document.hidden) { clearInterval(t); document.title = baseRef.current; } };
    window.addEventListener("focus", stop);
    document.addEventListener("visibilitychange", stop);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", stop);
      document.removeEventListener("visibilitychange", stop);
      if (baseRef.current !== null) document.title = baseRef.current;
    };
  }, [active, text]);
}

export function fmtWait(sec) {
  if (sec == null) return "";
  const m = Math.floor(sec / 60);
  if (m < 1) return "agora";
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

// ---------------------------------------------------------------- notas e etiquetas
export function NotesPanel({ phone, onChanged }) {
  const [data, setData] = useState({ tags: [], notes: [], suggested_tags: [] });
  const [text, setText] = useState("");
  const [tagText, setTagText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!phone) return;
    try {
      const { data: d } = await api.get(`/chat-sessions/${phone}/notes`);
      setData(d);
    } catch { /* sem notas ainda */ }
  }, [phone]);

  useEffect(() => { setData({ tags: [], notes: [], suggested_tags: [] }); setText(""); load(); }, [phone, load]);

  const saveTags = async (tags) => {
    setData((d) => ({ ...d, tags }));
    try {
      await api.put(`/chat-sessions/${phone}/tags`, { tags });
      onChanged?.();
    } catch (ex) { toast.error(formatApiError(ex)); load(); }
  };
  const toggleTag = (tg) => saveTags(data.tags.includes(tg) ? data.tags.filter((x) => x !== tg) : [...data.tags, tg]);
  const addTag = () => {
    const tg = tagText.trim();
    setTagText("");
    if (tg && !data.tags.includes(tg)) saveTags([...data.tags, tg]);
  };
  const addNote = async () => {
    const body = text.trim();
    if (!body || saving) return;
    setSaving(true);
    try {
      await api.post(`/chat-sessions/${phone}/notes`, { text: body });
      setText("");
      await load();
      onChanged?.();
    } catch (ex) { toast.error(formatApiError(ex)); } finally { setSaving(false); }
  };
  const delNote = async (id) => {
    try { await api.delete(`/chat-sessions/${phone}/notes/${id}`); await load(); onChanged?.(); }
    catch (ex) { toast.error(formatApiError(ex)); }
  };

  const options = Array.from(new Set([...(data.suggested_tags || []), ...data.tags]));
  return (
    <div className="mt-4 pt-3 border-t border-slate-800 space-y-3" data-testid="chat-notes-panel">
      <div>
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1.5">
          <Tag className="w-3 h-3" /> Etiquetas
        </div>
        <div className="flex flex-wrap gap-1">
          {options.map((tg) => {
            const on = data.tags.includes(tg);
            return (
              <button key={tg} type="button" onClick={() => toggleTag(tg)}
                className={`text-[11px] rounded-full border px-2 py-0.5 ${on
                  ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200"
                  : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
                {tg}
              </button>
            );
          })}
        </div>
        <div className="flex gap-1 mt-1.5">
          <Input value={tagText} onChange={(e) => setTagText(e.target.value)} placeholder="Nova etiqueta"
            maxLength={24} onKeyDown={(e) => e.key === "Enter" && addTag()}
            className="h-7 text-xs bg-slate-900 border-slate-700" />
          <Button type="button" size="sm" variant="outline" onClick={addTag} className="h-7 px-2 border-slate-700">
            <Plus className="w-3 h-3" />
          </Button>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1.5">
          <StickyNote className="w-3 h-3" /> Notas internas <span className="normal-case tracking-normal text-slate-600">(o cliente não vê)</span>
        </div>
        <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
          {data.notes.length === 0 && <div className="text-xs text-slate-600">Nenhuma nota.</div>}
          {data.notes.map((n) => (
            <div key={n.id} className="rounded-md bg-amber-500/10 border border-amber-500/25 px-2 py-1.5 text-xs text-amber-100">
              <div className="whitespace-pre-wrap break-words">{n.text}</div>
              <div className="flex items-center justify-between text-[10px] text-amber-200/60 mt-1">
                <span>{(n.by || "").split("@")[0]} · {new Date(n.at).toLocaleString("pt-BR", tzOpts())}</span>
                <button type="button" aria-label="Apagar nota" onClick={() => delNote(n.id)} className="hover:text-white">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={2000}
          placeholder="Escreva uma nota…"
          className="mt-1.5 w-full rounded-md bg-slate-900 border border-slate-700 px-2 py-1.5 text-xs text-slate-200 resize-none" />
        <Button type="button" size="sm" onClick={addNote} disabled={saving || !text.trim()}
          className="mt-1 h-7 text-xs bg-slate-800 hover:bg-slate-700 text-white w-full">
          Salvar nota
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- relatorio
const fmtDur = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
};

export function AttendanceReport() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/attendance/report", { params: { days } })
      .then((r) => { if (alive) setData(r.data); })
      .catch((ex) => toast.error(formatApiError(ex)))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const max = Math.max(1, ...(data?.per_day || []).map((d) => d.messages_in));
  const stat = (label, value, hint) => (
    <Card className="bg-slate-900/50 border-slate-800 p-4">
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className="font-outfit text-2xl text-white mt-1">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="attendance-report">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-emerald-400" />
        <div className="font-outfit text-white font-semibold">Relatório de atendimentos</div>
        <div className="ml-auto flex gap-1">
          {[7, 30, 90].map((d) => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)}
              className={days === d ? "bg-emerald-500 text-emerald-950 h-7 text-xs" : "border-slate-700 h-7 text-xs"}>
              {d} dias
            </Button>
          ))}
        </div>
      </div>
      {loading && !data && <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {stat("Pediram atendente", data.handoff_episodes, `${data.episodes} atendimentos no total`)}
            {stat("Espera média", fmtDur(data.avg_wait_s), data.max_wait_s != null ? `maior: ${fmtDur(data.max_wait_s)}` : "sem dados ainda")}
            {stat("Atendidos em até 5 min", data.within_5min_pct != null ? `${data.within_5min_pct}%` : "—")}
            {stat("Sem resposta", data.not_attended, "pediram e ninguém respondeu")}
          </div>
          {data.history_since && (
            <div className="text-[11px] text-slate-600">
              Tempos de espera contados desde {new Date(data.history_since).toLocaleDateString("pt-BR", tzOpts())} (início do registro).
              Mensagens e volume por dia vêm do histórico completo.
            </div>
          )}
          {!data.history_since && (
            <div className="text-[11px] text-slate-600">Os tempos de espera começam a ser registrados a partir de agora.</div>
          )}

          <Card className="bg-slate-900/50 border-slate-800 p-4">
            <div className="text-xs text-slate-400 mb-3">
              Mensagens recebidas por dia · {data.totals.messages_in} recebidas, {data.totals.replies} respostas da equipe
            </div>
            <div className="flex items-end gap-[2px] h-28">
              {data.per_day.map((d) => (
                <div key={d.date} className="flex-1 min-w-0 bg-emerald-500/70 hover:bg-emerald-400 rounded-t"
                  style={{ height: `${Math.max(2, (d.messages_in / max) * 100)}%` }}
                  title={`${d.date.split("-").reverse().join("/")}: ${d.messages_in} mensagens de ${d.clients} clientes, ${d.replies} respostas`} />
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-slate-600 mt-1">
              <span>{data.per_day[0]?.date.split("-").reverse().slice(0, 2).join("/")}</span>
              <span>{data.per_day[data.per_day.length - 1]?.date.split("-").reverse().slice(0, 2).join("/")}</span>
            </div>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800 p-4">
            <div className="text-xs text-slate-400 mb-2">Por atendente</div>
            {data.per_user.length === 0 ? (
              <div className="text-sm text-slate-600">Nenhuma resposta da equipe no período.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                  <tr><th className="text-left py-1.5">Atendente</th><th className="text-right">Atendimentos</th><th className="text-right">Respostas</th><th className="text-right">Espera média</th></tr>
                </thead>
                <tbody>
                  {data.per_user.map((u) => (
                    <tr key={u.user} className="border-b border-slate-800/60">
                      <td className="py-1.5 text-white">{u.user}</td>
                      <td className="text-right text-slate-300">{u.episodes}</td>
                      <td className="text-right text-slate-300">{u.replies}</td>
                      <td className="text-right text-slate-300">{fmtDur(u.avg_wait_s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
